import React from 'react';
import {
  Box,
  Button,
  FooterActionBar,
  Icon,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  SodAccountChip,
  SodActVsPotChip,
  SodEndPointChip,
  SodRiskPriorityChip,
  SodUserChip,
} from '../../../../shared/components';
import { CF_NEU_BLD } from '../../../../shared/constants';
import { useInvertedLocalize } from '../../../../shared/hooks';
import { type ListRowState } from '../../../../shared/types';

import './ViolationsGraphClusterShared.css';

interface ClusterCardProps {
  row: ListRowState;
  onClick: () => void;
  isLast?: boolean;
  className?: string;
}

/**
 * ClusterCard
 */
function ClusterCard({ row, onClick, isLast, className }: ClusterCardProps) {
  const accountCount = row.accounts.length;

  const listItemClass = cx(
    'ClusterCard',
    !isLast && 'ClusterCard--isNotLast',
    className
  );

  return (
    <div
      className={listItemClass}
      onClick={onClick}
      onKeyDown={onClick}
      tabIndex={0}
      role='button'>
      <Typography kind='h3' color={CF_NEU_BLD} lineLimit={1}>
        {row.risk.riskName}
      </Typography>
      <Box tag='div' className='ClusterCard-details'>
        <SodRiskPriorityChip
          priority={row.risk.riskSeverity ?? row.risk.riskPriority}
          riskScore={row.risk.riskScore}
        />
        <SodActVsPotChip key='SodActVsPotChip' value={row.actualVsPotential} />
        {accountCount > 0 && (
          <SodEndPointChip
            endpoints={row.endpoints.map((endpoint) => ({
              endpointName: endpoint.endpointName,
              endpointLogo: endpoint.endpointLogo,
            }))}
          />
        )}
        <SodUserChip
          firstName={row.user.firstName}
          lastName={row.user.lastName}
          userName={row.user.username}
          isPrefixInterpoint={accountCount > 0}
        />
        {accountCount > 0 && (
          <SodAccountChip
            visibleAccountName={row.accounts[0].accountName}
            accountCount={accountCount}
            isPrefixInterpoint={false}
          />
        )}
      </Box>
    </div>
  );
}

ClusterCard.defaultProps = {
  isLast: false,
  className: null,
};

interface ViolationsInsightsFooterProps {
  isGraphMode?: boolean;
  onClick?: () => void;
}

/**
 * ViolationsInsightsFooter
 */
function ViolationsInsightsFooter({
  isGraphMode,
  onClick,
}: ViolationsInsightsFooterProps) {
  const l = useInvertedLocalize();

  return (
    <FooterActionBar className='Sod-ViolationsInsights-footer'>
      <Button
        onClick={onClick}
        kind='subtle'
        leftIcon={isGraphMode ? <Icon kind='List' /> : <Icon kind='Analyze' />}>
        {l(isGraphMode ? 'View List' : 'View Graph')}
      </Button>
    </FooterActionBar>
  );
}

ViolationsInsightsFooter.defaultProps = {
  isGraphMode: false,
  onClick: () => {},
};

export { ClusterCard, ViolationsInsightsFooter };
